// Wave 20.22 — Per-second countdown for a Drop Link's expiration.
//
// Ticks every 1000ms while the component is mounted; formats as
// the largest units first ("2d 3h", "23:12:45", "45m 12s",
// "0:47"). Falls through to "Expired" when the deadline passes;
// clears its own interval on unmount.

import React, { useEffect, useState } from 'react';

interface Props {
  expiresAt: string | null;
  /** Optional prefix — e.g. "Drop Link expires in ". Kept out of
   *  the component so callers can style it separately. */
  prefix?: string;
  /** Renders "Expired" once the deadline passes. Default true. */
  showExpired?: boolean;
}

export default function ExpirationCountdown({
  expiresAt,
  prefix,
  showExpired = true,
}: Props) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  if (!expiresAt) return null;
  const remaining = new Date(expiresAt).getTime() - Date.now();
  if (remaining <= 0) {
    if (!showExpired) return null;
    return <>Expired</>;
  }
  return (
    <>
      {prefix ?? ''}
      {formatRemaining(remaining)}
    </>
  );
}

export function formatRemaining(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const days = Math.floor(totalSec / 86_400);
  const hours = Math.floor((totalSec % 86_400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  if (days > 0) {
    // > 1 day: coarser display; per-second tick still fires but
    // the string only changes when a minute rolls over.
    return `${days}d ${hours}h ${minutes}m`;
  }
  if (hours > 0) {
    // Format as HH:MM:SS so it reads like a real countdown.
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  if (minutes > 0) {
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  }
  return `${seconds}s`;
}
